import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ValueLink from 'sb-valuelink'
import actions from 'redux/actions'
import storeData from 'sb-store-data'
import { connect } from 'redaction'
import { filter, upperFirst, toArray } from 'lodash'
import { date, getFrequencyNameByShippingPeriod, links, constants, compose } from 'common/helpers'
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'sb-react-intl'

import injectGlobals from 'decorators/injectGlobals'

import ConfirmModal from 'components/modal/ConfirmModal/ConfirmModal'
import RadioGroup from 'components/forms/RadioGroup/RadioGroup'
import RadioButton from 'components/forms/RadioButton/RadioButton'

import messages from './messages'
import s from './ChangeFrequencyModal.scss'


const getMessage = ({ field, frequencyName }) => messages[`${frequencyName}${field ? upperFirst(field) : ''}`]

class ChangeFrequencyModal extends Component {

  static propTypes = {
    name: PropTypes.string.isRequired,
    data: PropTypes.object,
  }

  static defaultProps = {
    name: constants.modals.ChangeFrequency,
  }

  constructor(props) {
    super()

    const { data: { frequencies } } = props
    const { name } = toArray(frequencies)[0]

    this.state = {
      activeFrequencyName: name,
      isErrorMessageVisible: false,
    }
  }

  handleConfirm = (planName) => {
    const { activeFrequencyName } = this.state
    const {
      name, data: { frequencies }, isCancelled, isPaused, modalData, intl,
      isResubscribeReminderPopupEnabled,
    } = this.props

    const popupName = modalData.popup

    const { nextShippingDate, shippingPeriod } = frequencies.find((plan) => plan.name === planName)

    if (isCancelled && isResubscribeReminderPopupEnabled) {
      return actions.models.subscription.resubscribe({
        plan: activeFrequencyName,
        withErrorHandler: false,
        popup: popupName,
      })
        .then(({ premiumProduct }) => {
          if (premiumProduct) {
            actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
              item: premiumProduct,
            })
          }
          else {
            actions.models.modals.open(constants.modals.resubscribe.SuccessfullyResubscribed)
          }

          actions.models.modals.close(constants.modals.ChangeFrequency)
        }, ({ statusCode, error }) => {
          if (statusCode === '409' && error === 'change_plan') {
            return this.setState({
              isErrorMessageVisible: true,
            })
          }

          const subscribePageTitle = getMessage({
            field: 'subscribePageTitle',
            frequencyName: activeFrequencyName.toLowerCase(),
          })

          const subscribePageSubTitle = getMessage({
            field: 'subscribePageSubTitle',
            frequencyName: activeFrequencyName.toLowerCase(),
          })

          const subscribePageOrderInfoText = getMessage({
            field: 'subscribePageOrderInfoText',
            frequencyName: activeFrequencyName.toLowerCase(),
          })

          actions.misc.cookies.setItem('reactivateSubscription', {
            messageTitle: subscribePageTitle,
            messageSubTitle: subscribePageSubTitle,
            text: intl.formatMessage(subscribePageOrderInfoText),
            orderInfoResubscribeMessage: subscribePageOrderInfoText,
            planName: activeFrequencyName,
          })

          actions.misc.cookies.removeItem(constants.cookieNames.offer)
          actions.misc.cookies.removeItem(constants.cookieNames.discountCoupon)

          this.handleCancel()
          actions.models.modals.close(constants.modals.resubscribe.GetBackTogether)
          actions.models.router.push(links.subscribe)
        })
    }

    actions.models.ui.showRequestLoader()
    actions.models.subscription.changePlan(activeFrequencyName, popupName)
      .then(() => {
        actions.models.ui.hideRequestLoader()
        actions.models.modals.close(name)
        actions.models.modals.open(constants.modals.FrequencyChangeSuccess, { nextShippingDate, shippingPeriod })
      }, ({ statusCode }) => {
        actions.models.ui.hideRequestLoader()

        if (statusCode !== '409' || !isPaused) {
          return this.setState({
            isErrorMessageVisible: true,
          })
        }

        // TODO seems it's unreachable
        // it's impossible to change plan with 'paused' subscription
        // checked by sonatskiy & eastashevich
        actions.models.payment.setUnpause(true)
        actions.models.router.push(links.cardUpdate)
      })
  }

  handleCancel = () => {
    const { name, modalData: { onCancel } } = this.props

    if (typeof onCancel === 'function') {
      onCancel()
    }

    actions.models.modals.close(name)
  }

  render() {
    const { name, data: { frequencies, modalMessage, periodLineMessage } } = this.props
    const { isErrorMessageVisible } = this.state

    const activeFrequencyNameLink = ValueLink.state(this, 'activeFrequencyName')

    return (
      <ConfirmModal
        name={name}
        title={modalMessage}
        onConfirm={() => this.handleConfirm(activeFrequencyNameLink.value)}
        onCancel={this.handleCancel}
      >
        <FormattedMessage tag="div" {...periodLineMessage}>
          {
            (periodLine) => (
              <FormattedMessage {...messages.text} values={{ periodLine }} />
            )
          }
        </FormattedMessage>
        <div className={s.content}>
          <div>
            <RadioGroup className={s.radioButtons} valueLink={activeFrequencyNameLink}>
              {
                toArray(frequencies).map((item) => {
                  const { name, nextShippingDate, nextBillingDate, shippingPeriod } = item

                  const shippingDate  = date.formatDate(nextShippingDate, 'MMM DD', true)
                  const billingDate   = date.formatDate(nextBillingDate, 'MMM DD', true)
                  const frequencyName = getFrequencyNameByShippingPeriod(shippingPeriod)

                  return (
                    <RadioButton
                      key={name}
                      className={s.frequencyItem}
                      brand
                      value={name}
                      dataTestId={constants.testIds.changeFrequency.option}
                    >
                      <div className={s.upper}>
                        <FormattedMessage {...getMessage({ frequencyName })}>
                          {
                            (period) => (
                              <FormattedMessage {...messages.pleaseText} values={{ period }} />
                            )
                          }
                        </FormattedMessage>
                      </div>
                      {
                        shippingDate && (
                          <FormattedHTMLMessage
                            tag="div"
                            {...messages.nextShippingDate}
                            values={{ date: shippingDate }}
                          />
                        )
                      }
                      {
                        billingDate && (
                          <FormattedHTMLMessage
                            tag="div"
                            {...messages.nextBillingDate}
                            values={{ date: billingDate }}
                          />
                        )
                      }
                    </RadioButton>
                  )
                })
              }
            </RadioGroup>
            {
              isErrorMessageVisible && (
                <FormattedMessage tag="div" className={s.error} {...messages.changePlanErrorMessage} />
              )
            }
          </div>
        </div>
      </ConfirmModal>
    )
  }
}


export default compose(
  injectGlobals(({ getFeatureToggle }) => ({
    isResubscribeReminderPopupEnabled: getFeatureToggle(constants.features.resubscribeReminderPopup),
  })),
  storeData({
    data: (state, props) => {
      const { models: { subscription: { isCancelled } } } = state
      const { isResubscribeReminderPopupEnabled } = props

      if (isCancelled && isResubscribeReminderPopupEnabled) {
        return actions.models.subscription.fetchFrequenciesForResubscribeUser()
          .then((result) => Object.values(result))
          .then((frequencies) => ({
            frequencies,
            modalMessage: getMessage({ field: 'title', frequencyName: 'triMonthly' }),
            periodLineMessage: getMessage({ field: 'periodLine', frequencyName: 'triMonthly' }),
          }))
      }

      return actions.models.subscription.fetchFrequencies()
        .then((result) => filter(result, ({ selected }) => !selected))
        .then((filteredFrequencies) => {
          const frequencyName = getFrequencyNameByShippingPeriod(filteredFrequencies[0].shippingPeriod)

          return {
            frequencies: filteredFrequencies,
            modalMessage: getMessage({ field: 'title', frequencyName }),
            periodLineMessage: getMessage({ field: 'periodLine', frequencyName }),
          }
        })
    },
  }),
  connect({
    isPaused: 'models.subscription.isPaused',
    isCancelled: 'models.subscription.isCancelled',
    modalData: (state) => state.models.modals[constants.modals.ChangeFrequency].data,
  }),
  injectIntl,
)(ChangeFrequencyModal)
